import * as THREE from 'three';

/** Materials shared by every preview voxel model (vertex colors carry the palette + baked AO). */
export const previewMaterials = {
  lit: new THREE.MeshLambertMaterial({ vertexColors: true, fog: false }),
  glow: new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false, fog: false }),
};

/** Studio lighting: sky/ground fill, warm key from the front-right and a cool rim from behind. */
export function addStudioLights(scene: THREE.Scene, strength = 1): void {
  const hemi = new THREE.HemisphereLight(0xdfe9ff, 0x4a4032, 1.55 * strength);
  const key = new THREE.DirectionalLight(0xfff4e2, 2.1 * strength);
  key.position.set(-1.6, 2.6, -2.4);
  const rim = new THREE.DirectionalLight(0x9cc4ff, 1.5 * strength);
  rim.position.set(2.2, 1.6, 2.6);
  scene.add(hemi, key, rim);
}

let blobTexture: THREE.Texture | null = null;

function shadowTexture(): THREE.Texture {
  if (blobTexture) return blobTexture;
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  grad.addColorStop(0, 'rgba(0,0,0,0.62)');
  grad.addColorStop(0.45, 'rgba(0,0,0,0.38)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  blobTexture = new THREE.CanvasTexture(c);
  blobTexture.colorSpace = THREE.SRGBColorSpace;
  return blobTexture;
}

let blobGeometry: THREE.PlaneGeometry | null = null;
const blobMaterials = new Map<number, THREE.MeshBasicMaterial>();

/** Soft round contact shadow lying on the y = 0 plane (scale x/z to taste). */
export function makeShadowBlob(sizeX: number, sizeZ: number, opacity = 1): THREE.Mesh {
  blobGeometry ??= new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
  const key = Math.round(opacity * 100);
  let mat = blobMaterials.get(key);
  if (!mat) {
    mat = new THREE.MeshBasicMaterial({ map: shadowTexture(), transparent: true, opacity: key / 100, depthWrite: false, toneMapped: false, fog: false });
    blobMaterials.set(key, mat);
  }
  const m = new THREE.Mesh(blobGeometry, mat);
  m.scale.set(sizeX, 1, sizeZ);
  m.renderOrder = 1;
  return m;
}

interface PedestalParts {
  base: THREE.CylinderGeometry;
  top: THREE.CylinderGeometry;
  ring: THREE.RingGeometry;
  baseMat: THREE.MeshLambertMaterial;
  topMat: THREE.MeshLambertMaterial;
  ringMat: THREE.MeshBasicMaterial;
}
let pedestalParts: PedestalParts | null = null;

/** Low octagonal plinth with a faint accent rim; its top surface is at y = 0. */
export function makePedestal(radius = 0.62): THREE.Group {
  pedestalParts ??= {
    base: new THREE.CylinderGeometry(1, 1.08, 1, 8),
    top: new THREE.CylinderGeometry(1, 1, 1, 8),
    ring: new THREE.RingGeometry(0.93, 0.975, 8, 1),
    baseMat: new THREE.MeshLambertMaterial({ color: 0x232c3d, flatShading: true, fog: false }),
    topMat: new THREE.MeshLambertMaterial({ color: 0x323d53, flatShading: true, fog: false }),
    ringMat: new THREE.MeshBasicMaterial({ color: 0xffd24a, transparent: true, opacity: 0.55, depthWrite: false, toneMapped: false, fog: false }),
  };
  const p = pedestalParts;
  const g = new THREE.Group();
  const base = new THREE.Mesh(p.base, p.baseMat);
  base.scale.set(radius, 0.1, radius);
  base.position.y = -0.062;
  const top = new THREE.Mesh(p.top, p.topMat);
  top.scale.set(radius * 0.94, 0.024, radius * 0.94);
  top.position.y = -0.012;
  const ring = new THREE.Mesh(p.ring, p.ringMat);
  ring.rotation.x = -Math.PI / 2;
  ring.scale.setScalar(radius);
  ring.position.y = 0.0015;
  ring.renderOrder = 2;
  g.add(base, top, ring);
  // Put a flat side toward the camera.
  g.rotation.y = Math.PI / 8;
  return g;
}
